import { ReactNode, useCallback, useMemo } from 'react';

import { CareServiceReportLink } from '@/shared/model';

import { CarePlanServiceContext } from './care-plan-service.context';
import { CarePlanServiceState } from './care-plan-service.model';

export interface CarePlanServiceProviderProps {
  children: ReactNode;
  links: CareServiceReportLink[];
}

export function CarePlanServiceProvider({ children, links = [] }: CarePlanServiceProviderProps) {
  const getLinkByOrgan = useCallback(
    (organ: string) => links.find(({ key }) => key === organ),
    [links],
  );

  const getLinkByOrganWithSub = useCallback(
    (organ: string): CareServiceReportLink | undefined => {
      const reportSubLinks: CareServiceReportLink[] = links.flatMap((l) => l.sub ?? []);

      while (reportSubLinks.length) {
        const reportSubLink = reportSubLinks.pop()!;

        if (reportSubLink.key === organ) {
          return reportSubLink;
        }

        if (reportSubLink.sub?.length) {
          reportSubLinks.push(...reportSubLink.sub);
        }
      }

      return undefined;
    },
    [links],
  );

  const value: CarePlanServiceState = useMemo(
    () => ({
      getLinkByOrgan,
      getLinkByOrganWithSub,
      links,
    }),
    [links, getLinkByOrgan, getLinkByOrganWithSub],
  );

  return <CarePlanServiceContext value={value}>{children}</CarePlanServiceContext>;
}
